/**
 * Apply GET /v1/config theme.accent_color onto the CSS token layer.
 *
 * No color values are declared here — every derived token is expressed as an
 * opacity mix of the config accent (`color-mix(... , transparent)`), with a
 * small white mix only at the far end of gradients.
 */

/** Accent knob every override below is derived from. */
const ACCENT_VAR = '--avatar-accent';

/** Opacity of the accent, as a share of `transparent`. */
function accentAlpha(percent: number): string {
  return `color-mix(in srgb, var(${ACCENT_VAR}) ${percent}%, transparent)`;
}

/** Accent lifted with a touch of white — gradient tail only. */
function accentLift(percent: number): string {
  return `color-mix(in srgb, var(${ACCENT_VAR}) ${percent}%, white)`;
}

/** Derived tokens, in the order tokens.css declares them. */
function accentOverrides(): Record<string, string> {
  return {
    // Brand ramp: one hue, progressively lifted toward the gradient tail
    '--avatar-color-brand-red': `var(${ACCENT_VAR})`,
    '--avatar-color-brand-orange': accentLift(88),
    '--avatar-color-brand-yellow': accentLift(72),
    '--avatar-color-brand-orange-deep': `var(${ACCENT_VAR})`,
    // Panel surfaces: a whisper of accent on the page (keep chips lighter)
    '--avatar-color-cream': accentLift(4),
    '--avatar-color-cream-soft': accentLift(2),
    '--avatar-alpha-brand-35': accentAlpha(35),
    '--avatar-alpha-orange-30': accentAlpha(30),
    // Suggestion chip icons — separated by opacity, not by hue
    '--avatar-icon-robot-bg': accentAlpha(10),
    '--avatar-icon-sparkles-bg': accentAlpha(14),
    '--avatar-icon-target-bg': accentAlpha(8),
    '--avatar-icon-rocket-bg': accentAlpha(12),
    '--avatar-icon-accent': `var(${ACCENT_VAR})`,
    '--avatar-icon-accent-warm': accentLift(88),
    '--avatar-focus-ring': `var(${ACCENT_VAR})`,
  };
}

function isSupportedColor(value: string): boolean {
  if (typeof CSS === 'undefined' || !CSS.supports) return false;
  return CSS.supports('color', value);
}

/**
 * Point the token layer at the config accent (e.g. `#1E5BFF`).
 * Invalid / missing values are ignored so tokens.css defaults stay.
 */
export function applyConfigAccent(
  accentColor: string | undefined,
  root: HTMLElement = document.documentElement,
): void {
  const accent = accentColor?.trim();
  if (!accent || !isSupportedColor(accent)) return;

  root.style.setProperty(ACCENT_VAR, accent);

  // Without color-mix the accent still applies; derived tints keep defaults.
  if (!isSupportedColor(accentAlpha(30))) {
    root.style.setProperty('--avatar-color-brand-red', `var(${ACCENT_VAR})`);
    root.style.setProperty('--avatar-icon-accent', `var(${ACCENT_VAR})`);
    root.style.setProperty('--avatar-icon-accent-warm', `var(${ACCENT_VAR})`);
    return;
  }

  const overrides = accentOverrides();
  for (const [token, value] of Object.entries(overrides)) {
    root.style.setProperty(token, value);
  }
}

export function clearConfigAccent(
  root: HTMLElement = document.documentElement,
): void {
  root.style.removeProperty(ACCENT_VAR);
  for (const token of Object.keys(accentOverrides())) {
    root.style.removeProperty(token);
  }
}

/** Pick a panel locale from GET /v1/config (host cannot override). */
export function resolveConfigLocale(options: {
  localeDefault?: string;
  localesSupported?: string[];
}): string | null {
  const preferred = options.localeDefault?.trim();
  if (!preferred) return null;

  const supported = options.localesSupported ?? [];
  if (supported.length === 0) return preferred;

  const normalized = preferred.replace(/_/g, '-').toLowerCase();
  const base = normalized.split('-')[0] || normalized;
  const allowed = supported.some((item) => {
    const n = item.replace(/_/g, '-').toLowerCase();
    return n === normalized || n.split('-')[0] === base;
  });

  return allowed ? preferred : null;
}
